
import { synczbase } from '../lib/synczbase';
import { UserProfile, UserRole } from '../types';
import { getRoleForEmail } from '../utils/envAuth';

export const createUserProfile = async (
    user: any, 
    role?: UserRole, 
    tenantId?: string,
    appId?: string,
    parentId?: string
): Promise<any> => {
    const uid = user.id || user.uid || ('usr_' + Date.now());
    const userEmail = (user.email || '').toLowerCase().trim();
    const targetAppId = appId || user.appId || tenantId || 'default';
    const targetParentId = parentId || user.parentId || null;
    const targetRole = role || user.role || 'CLIENT';
    
    // Check if user already exists by ID or EMAIL in this appId
    let existing: any[] = [];
    if (uid && userEmail) {
        existing = await synczbase.query('SELECT * FROM users WHERE id = $1 OR (LOWER(email) = $2 AND ("appId" = $3 OR "appId" IS NULL)) LIMIT 1', [uid, userEmail, targetAppId]);
    } else if (uid) {
        existing = await synczbase.query("SELECT * FROM users WHERE id = $1 LIMIT 1", [uid]);
    } else if (userEmail) {
        existing = await synczbase.query('SELECT * FROM users WHERE LOWER(email) = $1 AND ("appId" = $2 OR "appId" IS NULL) LIMIT 1', [userEmail, targetAppId]);
    }

    let token = synczbase.getToken();
    if (!token) {
        token = 'syncz_token_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
        synczbase.setToken(token);
    }

    if (existing && existing.length > 0) {
        const row = existing[0] as any;
        let profile = row;
        if (row.data) {
            try { profile = typeof row.data === 'string' ? JSON.parse(row.data) : row.data; } catch(e) {}
        }

        const targetUid = row.id || row.uid || uid;
        const storedRole = row.role || profile.role;
        const envRole = getRoleForEmail(userEmail || row.email || profile.email, storedRole || targetRole);
        const finalRole = (envRole === 'SUPER_ADMIN' || envRole === 'OWNER') ? envRole : (storedRole || targetRole);
        const displayName = user.displayName || profile.displayName || profile.name || 'User';

        const updatedProfile = {
            ...profile,
            uid: targetUid,
            id: targetUid,
            email: userEmail || profile.email || '',
            displayName: displayName,
            role: finalRole,
            appId: row.appId || profile.appId || targetAppId,
            parentId: row.parentId || profile.parentId || targetParentId,
            tenantId: tenantId || profile.tenantId || 'default',
            synczToken: token,
            syncz_token: token,
        };

        try {
            await synczbase.query(
                `UPDATE users SET email = $1, role = $2, "appId" = $3, "parentId" = $4, data = $5 WHERE id = $6`,
                [updatedProfile.email, updatedProfile.role, updatedProfile.appId, updatedProfile.parentId, JSON.stringify(updatedProfile), targetUid]
            );
        } catch(e) {
            await synczbase.query(
                "UPDATE users SET data = $1 WHERE id = $2",
                [JSON.stringify(updatedProfile), targetUid]
            );
        }
        try {
            await synczbase.apiCall('/api/auth/sync-user', 'POST', updatedProfile);
        } catch (sErr) {}
        return updatedProfile;
    } else {
        const envRole = getRoleForEmail(userEmail, targetRole);
        const finalRole = (envRole === 'SUPER_ADMIN' || envRole === 'OWNER') ? envRole : targetRole;
        const displayName = user.displayName || (finalRole === 'SUPER_ADMIN' ? 'Super Admin' : (finalRole === 'OWNER' ? 'Workspace Owner' : 'New User'));
        const createdAt = Date.now();
        const finalTenantId = tenantId || 'default';

        const newUser: UserProfile & { synczToken?: string; syncz_token?: string } = {
            uid: uid,
            id: uid,
            email: userEmail,
            displayName: displayName,
            role: finalRole as UserRole,
            appId: targetAppId,
            parentId: targetParentId || undefined,
            createdAt: createdAt,
            tenantId: finalTenantId,
            synczToken: token,
            syncz_token: token
        };

        try {
            await synczbase.query(
                `INSERT INTO users (id, uid, email, role, "appId", "parentId", "displayName", "createdAt", data) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9)`,
                [uid, uid, newUser.email, newUser.role, targetAppId, targetParentId, displayName, createdAt, JSON.stringify(newUser)]
            );
        } catch(err) {
            await synczbase.query(
                "INSERT INTO users (id, data) VALUES ($1, $2)",
                [uid, JSON.stringify(newUser)]
            );
        }
        try {
            await synczbase.apiCall('/api/auth/sync-user', 'POST', newUser);
        } catch (sErr) {}
        return newUser;
    }
};

export const loginWithEmail = async (email?: string, password?: string, tenantId?: string, role?: UserRole, appId?: string): Promise<any | null> => {
    if (!email || !password) throw new Error("Email and password required");
    try {
        const cleanEmail = email.toLowerCase().trim();
        const targetAppId = appId || tenantId || 'default';

        const result = await synczbase.apiCall('/api/auth/login', 'POST', { email: cleanEmail, password, appId: targetAppId });
        
        let token = result.token;
        if (!token) {
            token = 'syncz_token_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
        }
        synczbase.setToken(token);

        let userObj = result.user || { email: cleanEmail, id: token, role: role || 'CLIENT', appId: targetAppId };
        userObj.email = cleanEmail;

        const profile = await createUserProfile(userObj, userObj.role || role, tenantId, targetAppId);
        return profile;
    } catch (e) {
        console.error("Login failed", e);
        throw e;
    }
};

export const signupWithEmail = async (email?: string, password?: string, tenantId?: string, role?: UserRole, appId?: string, parentId?: string): Promise<any | null> => {
    if (!email || !password) throw new Error("Email and password required");
    try {
        const cleanEmail = email.toLowerCase().trim();
        const targetAppId = appId || tenantId || 'default';

        const result = await synczbase.apiCall('/api/auth/signup', 'POST', {
            email: cleanEmail,
            password,
            appId: targetAppId,
            parentId,
            role: role || 'CLIENT'
        });
        
        let token = result.token;
        if (!token) {
            token = 'syncz_token_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
        }
        synczbase.setToken(token);

        let userObj = result.user || { email: cleanEmail, id: token, role: role || 'CLIENT', appId: targetAppId, parentId };
        userObj.email = cleanEmail;

        const profile = await createUserProfile(userObj, userObj.role || role || 'CLIENT', tenantId, targetAppId, parentId);
        return profile;
    } catch (e) {
        console.error("Signup failed", e);
        throw e;
    }
};

export const getCurrentUser = async (): Promise<any | null> => {
    try {
        const token = synczbase.getToken();
        if (!token) return null;

        // Try /api/auth/me first as primary auth verification
        try {
            const res = await synczbase.apiCall('/api/auth/me', 'GET');
            if (res && (res.id || res.uid)) {
                const email = (res.email || '').toLowerCase().trim();
                const envRole = getRoleForEmail(email, res.role || 'CLIENT');
                const finalRole = (res.role === 'SUPER_ADMIN' || res.role === 'OWNER') ? res.role : envRole;

                return {
                    ...res,
                    uid: res.uid || res.id,
                    id: res.id || res.uid,
                    email: email,
                    role: finalRole,
                    appId: res.appId || 'default',
                    parentId: res.parentId || undefined,
                    displayName: res.displayName || (email ? email.split('@')[0] : 'User')
                };
            }
        } catch(e) {}

        // Fallback: Check DB directly for user matching id, uid, or token
        let dbUsers: any[] = [];
        try {
            dbUsers = await synczbase.query("SELECT * FROM users WHERE id = $1 OR uid = $1 OR LOWER(email) = LOWER($1) LIMIT 1", [token]);
        } catch(e) {}

        if (!dbUsers || dbUsers.length === 0) {
            try {
                const allUsers = await synczbase.query("SELECT * FROM users");
                dbUsers = (allUsers || []).filter((u: any) => {
                    let p = u;
                    if (u.data) {
                        try { p = typeof u.data === 'string' ? JSON.parse(u.data) : u.data; } catch(err) {}
                    }
                    return u.id === token || u.uid === token || u.email === token || p.syncz_token === token || p.synczToken === token || p.token === token;
                });
            } catch(e) {}
        }

        if (dbUsers && dbUsers.length > 0) {
            const row = dbUsers[0] as any;
            let p = row;
            if (row.data) {
                try { p = typeof row.data === 'string' ? JSON.parse(row.data) : row.data; } catch(e) {}
            }
            const email = (row.email || p.email || '').toLowerCase().trim();
            const storedRole = row.role || p.role || 'CLIENT';
            const finalRole = getRoleForEmail(email, storedRole as UserRole);

            return {
                uid: row.id || row.uid || p.uid || p.id,
                id: row.id || row.uid || p.uid || p.id,
                email: email,
                role: finalRole,
                appId: row.appId || p.appId || 'default',
                parentId: row.parentId || p.parentId || undefined,
                displayName: row.displayName || p.displayName || p.name || 'User'
            };
        }

        return null;
    } catch {
        return null;
    }
};
